'use strict';

/**
 * FeedbackPicker -- a reusable, filterable, multi-select feedback list for a modal.
 *
 * It is the feedback twin of the "bundle other work items" picker in the Generate-prompt dialog
 * (PictView-PromptAttachments _gen*): a search box, facet chips (Unaddressed only + the SourceType
 * lenses), a sortable metadata table, and a selected-chips row. Filtering rides on FocusFilters (which
 * already knows the Feedback facets: q / addressed / sourcetype / customer), so this is mostly the UI.
 *
 * It renders into a host element and uses event delegation (no inline onclick, no global handles), so
 * several instances can live at once and it drops cleanly into any Pict-Section-Modal body. The caller
 * mounts it after the modal DOM exists and reads selectedIDs() when the person confirms.
 *
 *   let tmpPicker = new FeedbackPicker();
 *   tmpPicker.configure(pAllFeedbackRows, { Exclude: [pIDSource], Selected: [] });
 *   tmpPicker.mount(document.getElementById('host'));
 *   ... on OK: tmpPicker.selectedIDs()  // [IDFeedback, ...] in the order picked
 */

const libFocus = require('./FocusFilters.js');

// The Feedback SourceType lenses (mirrors the model's SourceType enum), rendered as facet chips.
const _SOURCE_TYPES = ['Test Run', 'Customer Conversation', 'Developer Experience', 'Field Trial', 'Other'];
const _STYLE_ID = 'fbp-picker-styles';

function _esc(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh]));
}

function _truthy(pValue)
{
	return pValue === true || pValue === 1 || pValue === '1';
}

function _shortDate(pValue)
{
	let tmpDate = pValue ? new Date(pValue) : null;
	if (!tmpDate || isNaN(tmpDate.getTime())) { return ''; }
	return (tmpDate.getMonth() + 1) + '/' + tmpDate.getDate() + '/' + String(tmpDate.getFullYear()).slice(2);
}

// Injected once. Themed with the app --theme-color-* vars so it reads in light and dark, matching the
// work-item bundle picker (.ppa-pick-*) it is modeled on.
const _CSS =
	'.fbp-search { width: 100%; margin-bottom: 8px; }'
	+ '.fbp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 8px; }'
	+ '.fbp-chip { border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); border-radius: 999px; padding: 2px 11px; font-size: 11.5px; cursor: pointer; }'
	+ '.fbp-chip:hover { background: var(--theme-color-background-hover, #eaf2ff); }'
	+ '.fbp-chip.on { background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff); border-color: transparent; }'
	+ '.fbp-table { max-height: 42vh; overflow: auto; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; }'
	+ '.fbp-tbl { width: 100%; border-collapse: collapse; font-size: 12.5px; }'
	+ '.fbp-tbl th { position: sticky; top: 0; z-index: 1; background: var(--theme-color-background-panel, #fff); text-align: left; padding: 6px 10px; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee); }'
	+ '.fbp-tbl td { padding: 6px 10px; border-bottom: 1px solid var(--theme-color-border-default, #eef1f6); color: var(--theme-color-text-primary, #2c3140); white-space: nowrap; }'
	+ '.fbp-tbl tr:last-child td { border-bottom: none; }'
	+ '.fbp-row { cursor: pointer; }'
	+ '.fbp-row:hover td { background: var(--theme-color-background-hover, #eaf2ff); }'
	+ '.fbp-row.on td { background: var(--theme-color-background-secondary, #eef1f6); }'
	+ '.fbp-title { white-space: normal; min-width: 15rem; }'
	+ '.fbp-num, .fbp-src, .fbp-upd { color: var(--theme-color-text-secondary, #5b6376); }'
	+ '.fbp-cb { width: 1px; }'
	+ '.fbp-tag { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 5px; padding: 0 5px; margin-left: 6px; }'
	+ '.fbp-empty { text-align: center; color: var(--theme-color-text-secondary, #5b6376); padding: 16px; }'
	+ '.fbp-count { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); padding: 6px 4px 0; }'
	+ '.fbp-selected { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }'
	+ '.fbp-selchip { display: inline-flex; align-items: center; gap: 5px; max-width: 100%; background: var(--theme-color-background-secondary, #eef1f6); border-radius: 999px; padding: 2px 4px 2px 10px; font-size: 12px; }'
	+ '.fbp-selchip-t { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }'
	+ '.fbp-x { border: none; background: none; cursor: pointer; padding: 0 4px; font-size: 14px; line-height: 1; color: inherit; }';

function _injectCSS()
{
	if (typeof document === 'undefined') { return; }
	if (document.getElementById(_STYLE_ID)) { return; }
	let tmpStyle = document.createElement('style');
	tmpStyle.id = _STYLE_ID;
	tmpStyle.textContent = _CSS;
	document.head.appendChild(tmpStyle);
}

class FeedbackPicker
{
	constructor()
	{
		this._all = [];
		this._index = {};
		this._selected = [];
		this._filter = libFocus.emptyState();
		this._host = null;
	}

	// Load candidate feedback rows. Options: Exclude (ids to drop, e.g. the source feedback or the
	// already-linked set), Selected (ids to preselect). Returns this for chaining.
	configure(pRows, pOptions)
	{
		pOptions = pOptions || {};
		let tmpExclude = {};
		(pOptions.Exclude || []).forEach((pID) => { tmpExclude[Number(pID)] = true; });
		this._all = (pRows || []).filter((pRow) => pRow && !pRow.Deleted && !tmpExclude[Number(pRow.IDFeedback)]);
		this._index = {};
		this._all.forEach((pRow) => { this._index[pRow.IDFeedback] = { Number: pRow.FeedbackNumber || pRow.IDFeedback, Title: pRow.Title || '' }; });
		this._selected = (pOptions.Selected || []).map(Number).filter((pID) => pID > 0 && !tmpExclude[pID]);
		return this;
	}

	// The chosen feedback ids, in the order they were picked.
	selectedIDs()
	{
		return (this._selected || []).slice();
	}

	// Candidate rows after the live filter, newest-touched first (so the most relevant float up).
	visibleRows()
	{
		let tmpRows = libFocus.filterRows(this._all || [], this._filter || {}, { CurrentUserID: 0, Agents: {} });
		return tmpRows.slice().sort((pA, pB) =>
		{
			let tmpA = Date.parse(pA.UpdateDate || pA.CreateDate || '') || 0;
			let tmpB = Date.parse(pB.UpdateDate || pB.CreateDate || '') || 0;
			if (tmpA !== tmpB) { return tmpB - tmpA; }
			return (Number(pB.FeedbackNumber || 0) - Number(pA.FeedbackNumber || 0));
		});
	}

	// Render into pHostElement and wire delegated listeners (survive re-renders of the inner regions).
	mount(pHostElement)
	{
		if (!pHostElement) { return this; }
		_injectCSS();
		this._host = pHostElement;
		pHostElement.innerHTML =
			'<input class="fbp-search pict-input" placeholder="Search feedback...">'
			+ '<div class="fbp-chips"></div>'
			+ '<div class="fbp-table"></div>'
			+ '<div class="fbp-selected"></div>';
		let tmpSearch = pHostElement.querySelector('.fbp-search');
		if (tmpSearch) { tmpSearch.addEventListener('input', (pEvent) => { this._filter.q = (pEvent.target && pEvent.target.value) || ''; this._renderTable(); }); }
		let tmpChips = pHostElement.querySelector('.fbp-chips');
		if (tmpChips) { tmpChips.addEventListener('click', (pEvent) => { let tmpBtn = pEvent.target.closest('[data-facet]'); if (tmpBtn) { this._toggleFacet(tmpBtn.getAttribute('data-facet'), tmpBtn.getAttribute('data-value')); } }); }
		let tmpTable = pHostElement.querySelector('.fbp-table');
		if (tmpTable) { tmpTable.addEventListener('click', (pEvent) => { let tmpRow = pEvent.target.closest('[data-fb]'); if (tmpRow) { this._toggleRow(Number(tmpRow.getAttribute('data-fb'))); } }); }
		let tmpSelected = pHostElement.querySelector('.fbp-selected');
		if (tmpSelected) { tmpSelected.addEventListener('click', (pEvent) => { let tmpX = pEvent.target.closest('[data-remove]'); if (tmpX) { this._toggleRow(Number(tmpX.getAttribute('data-remove'))); } }); }
		this._renderChips();
		this._renderTable();
		this._renderSelected();
		return this;
	}

	_toggleFacet(pFacet, pValue)
	{
		if (pFacet === 'addressed') { this._filter.addressed = (this._filter.addressed === 'no') ? '' : 'no'; }
		else if (pFacet === 'sourcetype') { this._filter.sourcetype = (this._filter.sourcetype === pValue) ? '' : pValue; }
		this._renderChips();
		this._renderTable();
	}

	_toggleRow(pID)
	{
		pID = Number(pID) || 0;
		if (!pID) { return; }
		let tmpIndex = this._selected.indexOf(pID);
		if (tmpIndex >= 0) { this._selected.splice(tmpIndex, 1); } else { this._selected.push(pID); }
		this._renderTable();
		this._renderSelected();
	}

	_renderChips()
	{
		let tmpHost = this._host && this._host.querySelector('.fbp-chips');
		if (!tmpHost) { return; }
		let tmpHTML = '<button class="fbp-chip' + (this._filter.addressed === 'no' ? ' on' : '') + '" data-facet="addressed" data-value="no">Unaddressed only</button>';
		_SOURCE_TYPES.forEach((pType) =>
		{
			tmpHTML += '<button class="fbp-chip' + (this._filter.sourcetype === pType ? ' on' : '') + '" data-facet="sourcetype" data-value="' + _esc(pType) + '">' + _esc(pType) + '</button>';
		});
		tmpHost.innerHTML = tmpHTML;
	}

	_renderTable()
	{
		let tmpHost = this._host && this._host.querySelector('.fbp-table');
		if (!tmpHost) { return; }
		let tmpRows = this.visibleRows();
		let tmpSelectedSet = {};
		this._selected.forEach((pID) => { tmpSelectedSet[pID] = true; });
		let tmpBody = tmpRows.map((pRow) =>
		{
			let tmpID = Number(pRow.IDFeedback);
			let tmpOn = !!tmpSelectedSet[tmpID];
			let tmpTag = _truthy(pRow.Addressed) ? ' <span class="fbp-tag">addressed</span>' : '';
			return '<tr class="fbp-row' + (tmpOn ? ' on' : '') + '" data-fb="' + tmpID + '">'
				+ '<td class="fbp-cb"><input type="checkbox" tabindex="-1" ' + (tmpOn ? 'checked' : '') + '></td>'
				+ '<td class="fbp-num">#' + _esc(pRow.FeedbackNumber || tmpID) + '</td>'
				+ '<td class="fbp-title">' + _esc(pRow.Title || '') + tmpTag + '</td>'
				+ '<td class="fbp-src">' + _esc(pRow.SourceType || '') + '</td>'
				+ '<td class="fbp-upd">' + _esc(_shortDate(pRow.UpdateDate || pRow.CreateDate)) + '</td>'
				+ '</tr>';
		}).join('');
		if (!tmpBody) { tmpBody = '<tr><td colspan="5" class="fbp-empty">No feedback matches. Adjust the search or filters.</td></tr>'; }
		let tmpSelCount = this._selected.length;
		tmpHost.innerHTML = '<table class="fbp-tbl"><thead><tr><th></th><th>#</th><th>Title</th><th>Source</th><th>Updated</th></tr></thead><tbody>'
			+ tmpBody + '</tbody></table>'
			+ '<div class="fbp-count">' + tmpRows.length + ' shown' + (tmpSelCount ? (' &middot; ' + tmpSelCount + ' selected') : '') + '</div>';
	}

	_renderSelected()
	{
		let tmpHost = this._host && this._host.querySelector('.fbp-selected');
		if (!tmpHost) { return; }
		tmpHost.innerHTML = this._selected.map((pID) =>
		{
			let tmpMeta = this._index[pID] || { Number: pID, Title: '' };
			return '<span class="fbp-selchip"><span class="fbp-selchip-t">#' + _esc(tmpMeta.Number) + ' ' + _esc(tmpMeta.Title) + '</span>'
				+ '<button class="fbp-x" data-remove="' + pID + '" title="Remove">&times;</button></span>';
		}).join('');
	}
}

module.exports = FeedbackPicker;
